/**
 * Late-driver summary of today's Onfleet route ETAs (#2473), for the board's
 * en-route cell and next-gate line. Pure. A driver's finish is the latest
 * `estimatedCompletionTime` of their remaining tasks; a driver with no known
 * ETA is skipped. A driver is late when that finish is after the delivery
 * window end. S7b reuses this rule instead of writing its own.
 */
import type { components } from '@/types/api.generated';

type RouteEtaReport = components['schemas']['DispatchRouteEtaReport'];
type RouteEtaDriver = RouteEtaReport['drivers'][number];

export interface RouteEtaSummary {
  /** Drivers whose latest remaining ETA is after the window end. */
  late: number;
  /** `workerName` of the late driver whose finish is latest, or null (#2485). */
  latestLateName: string | null;
  /** The latest finish among late drivers (unix ms), or null. */
  latestLateMs: number | null;
  /** The latest finish among all drivers with an ETA (unix ms), or null. */
  latestMs: number | null;
}

/** A driver's finish: the latest known ETA of their remaining tasks (unix ms), or null. */
export function latestTaskEtaMs(driver: RouteEtaDriver): number | null {
  let latest: number | null = null;
  for (const task of driver.tasks) {
    const eta = task.estimatedCompletionTime;
    if (eta !== null) latest = Math.max(latest ?? eta, eta);
  }
  return latest;
}

export function summarizeRouteEta(report: RouteEtaReport, windowEndMs: number): RouteEtaSummary {
  let late = 0;
  let latestLateMs: number | null = null;
  let latestLateName: string | null = null;
  let latestMs: number | null = null;
  for (const driver of report.drivers) {
    const finish = latestTaskEtaMs(driver);
    if (finish === null) continue;
    latestMs = Math.max(latestMs ?? finish, finish);
    if (finish > windowEndMs) {
      late += 1;
      if (latestLateMs === null || finish > latestLateMs) {
        latestLateMs = finish;
        latestLateName = driver.workerName;
      }
    }
  }
  return { late, latestLateName, latestLateMs, latestMs };
}
